'use client';

import { useState } from 'react';
import type { SectoresData } from './schema';

export function SectoresRenderer({
  eyebrow,
  title,
  titleHl,
  subtitle,
  metaStats,
  sectors,
  ctaLabel = 'Ver casos del sector',
  ctaHref = '/portfolio',
}: SectoresData) {
  const [activeIdx, setActiveIdx] = useState(0);
  const safeSectors = Array.isArray(sectors) ? sectors : [];
  const safeMetaStats = Array.isArray(metaStats) ? metaStats : [];
  const active = safeSectors[activeIdx] ?? safeSectors[0];
  if (!active) return null;

  return (
    <section className="v5-light v5-section v5-sectores-section">
      <div className="v5-sectores-grid-bg"></div>
      <div className="container">
        <div className="v5-sectores-head">
          <div className="v5-sectores-head-left">
            {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
            <h2 className="v5-sectores-h2">
              {title}
              {titleHl ? (
                <>
                  <br />
                  <span className="v5-sectores-h2-hl">{titleHl}</span>
                </>
              ) : null}
            </h2>
          </div>
          <div className="v5-sectores-head-right">
            {subtitle ? <p className="v5-sectores-sub">{subtitle}</p> : null}
            {safeMetaStats.length > 0 ? (
              <div className="v5-sectores-meta">
                {safeMetaStats.map((s, i) => (
                  <div key={i} className="v5-sm-item">
                    <strong>{s.value}</strong>
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <div className="v5-sectores-pills" role="tablist" aria-label="Sectores en los que operamos">
          {safeSectors.map((s, i) => (
            <button
              key={s.slug}
              type="button"
              className={`v5-sect-pill${i === activeIdx ? ' is-active' : ''}`}
              role="tab"
              aria-selected={i === activeIdx}
              data-sector={s.slug}
              onClick={() => setActiveIdx(i)}
            >
              <span className="v5-sp-icon" aria-hidden="true">{s.icon}</span>
              <span className="v5-sp-name">{s.name}</span>
              <span className="v5-sp-count">{s.count}</span>
            </button>
          ))}
        </div>
        <div className="v5-sect-panel" role="tabpanel">
          <div className="v5-sp-grid">
            <div className="v5-sp-info">
              <div className="v5-sp-eyebrow">
                <span className="dot"></span>
                <span>{active.label ?? `Sector destacado · ${active.name}`}</span>
              </div>
              <h3 className="v5-sp-title">{active.title}</h3>
              <p className="v5-sp-desc">{active.description}</p>
              <div className="v5-sp-stats">
                <div className="v5-sp-stat">
                  <div className="num">{active.ops}</div>
                  <div className="lbl">Operaciones cerradas</div>
                </div>
                <div className="v5-sp-stat">
                  <div className="num">{active.ticket}</div>
                  <div className="lbl">Ticket medio</div>
                </div>
                <div className="v5-sp-stat">
                  <div className="num">{active.max}</div>
                  <div className="lbl">Operación mayor</div>
                </div>
              </div>
              <a href={ctaHref} className="v5-sp-cta">
                {ctaLabel}
                <span>→</span>
              </a>
            </div>
            <div className="v5-sp-cases">
              {(active.cases ?? []).map((c, j) => (
                <article key={j} className="v5-sp-case">
                  <div className="case-tag">{c.tag}</div>
                  <div className="case-name">{c.name}</div>
                  <div className="case-meta">{c.meta}</div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
